
import type { Schedulable } from '@empathyco/playboard-core-domain'
import type { PropType } from 'vue'
import { defineComponent } from 'vue'

import { DeviceEnum, PositionEnum, ScheduleModeEnum } from '../../enums'

import DatePicker from '../date-picker.component.vue'
import { getDatepickerI18nConfig, UiRadioGroup } from '../keen-ui'
import TransitionSize from '../transition-size.component.vue'

export default defineComponent({
  name: 'Schedule',
  components: {
    UiRadioGroup,
    TransitionSize,
    DatePicker,
  },
  props: {
    value: {
      type: Object as PropType<Schedulable['schedule']>,
      required: true,
    },
    orientation: {
      type: String as PropType<PositionEnum>, // PositionEnum.TOP, PositionEnum.LEFT
      default: PositionEnum.LEFT,
    },
    radioGroupOrientation: {
      type: String as PropType<PositionEnum>, // PositionEnum.TOP, PositionEnum.LEFT
      required: false,
      default: null,
    },
    disabled: Boolean,
    messages: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      mode: ScheduleModeEnum.ALWAYS_ACTIVE,
      PositionEnum,
      ScheduleModeEnum,
    }
  },
  computed: {
    datepickerGridClasses(): string[] {
      if (this.orientation === PositionEnum.LEFT) {
        return ['epb-grid-col-md-12', 'epb-grid-col-xl']
      }
      return ['epb-grid-col']
    },
    datepickerProps(): Record<string, any> {
      return {
        ...getDatepickerI18nConfig(this.$epb.locale),
        orientation: this.$epb.device > DeviceEnum.MOBILE ? 'landscape' : 'portrait',
        pickerType: this.$epb.device > DeviceEnum.MOBILE ? 'popover' : 'modal',
        includeTime: true,
        displayFrom: this.orientation,
        disabled: this.disabled || this.isAlwaysActiveMode,
        placeholder: this.isAlwaysActiveMode
          ? this.messages.fields.placeholders.always
          : this.messages.fields.placeholders.scheduled,
        messages: this.$epb.messages.app.date,
      }
    },
    isAlwaysActiveMode(): boolean {
      return this.mode === ScheduleModeEnum.ALWAYS_ACTIVE
    },
    activationDate(): Schedulable['schedule']['activationDate'] {
      return (!!this.value && this.value.activationDate) || undefined
    },
    deactivationDate(): Schedulable['schedule']['deactivationDate'] {
      return (!!this.value && this.value.deactivationDate) || undefined
    },
    radioGroupOptions(): { label: string; value: number }[] {
      const { always, schedule } = this.messages.fields.schedule.states
      return [
        {
          label: always,
          value: 1,
        },
        {
          label: schedule,
          value: 0,
        },
      ]
    },
    radioGroupOrientationComputed(): PositionEnum {
      return this.radioGroupOrientation ?? this.orientation
    },
  },
  watch: {
    value: {
      immediate: true,
      handler() {
        if (!!this.value && this.isAlwaysActiveMode) {
          this.mode =
            !this.value.activationDate && !this.value.deactivationDate
              ? ScheduleModeEnum.ALWAYS_ACTIVE
              : ScheduleModeEnum.SCHEDULED
        }
        this.$emit('input', this.value)
      },
      deep: true,
    },
  },
  methods: {
    onActivationDateChange(date: Date): void {
      this.value.activationDate = date
      this.$emit('change', this.value)
    },
    onDeactivationDateChange(date?: Date): void {
      if (!this.value.deactivationDate && !!date) {
        date = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59)
      }
      this.value.deactivationDate = date
      this.$emit('change', this.value)
    },
    resetValuesIfScheduleDisabled(): void {
      if (this.isAlwaysActiveMode) {
        this.value.activationDate = this.value.deactivationDate = undefined
        this.$emit('change', this.value)
      }
    },
  },
})
